import type { MegaMenuHeaderLanguageSwitcher } from "@multi-tenant-cms/types/menu";

import {
	isNavigationHrefCurrent,
	normalizeNavigationPath,
} from "../ui/lib/navigationMenuLinkStyles";

export type MegaMenuLocaleOption = {
	code: string;
	flag: string;
	label: string;
	/** Optional public URL or path for this locale/region (e.g. `/uk`). */
	href?: string;
};

/**
 * True when `pathname` matches a locale `href` (absolute URL pathname or
 * relative path), using exact or prefix match (same rules as nav current).
 */
export function pathMatchesLocaleHref(
	pathname: string | null | undefined,
	href: string | null | undefined
): boolean {
	return isNavigationHrefCurrent(href, pathname);
}

/** First non-empty path segment after normalize (e.g. `/uk/blog` → `uk`). */
export function firstPathSegment(
	pathname: string | null | undefined
): string | null {
	const normalized = normalizeNavigationPath(pathname);
	if (normalized === "/") {
		return null;
	}
	const segment = normalized.split("/").find((part) => part.length > 0);
	return segment ?? null;
}

/**
 * True when the locale `code` matches the first pathname segment
 * (case-insensitive). Useful for regional prefixes like `/uk/...` when
 * `code` is `UK` / `uk` and no `href` is set.
 */
export function localeCodeMatchesPathSegment(
	code: string,
	pathname: string | null | undefined
): boolean {
	const segment = firstPathSegment(pathname);
	const normalizedCode = code.trim();
	if (!(segment && normalizedCode)) {
		return false;
	}
	return segment.toLowerCase() === normalizedCode.toLowerCase();
}

/**
 * Resolve the current locale code from the public pathname.
 * Prefer the longest matching `href`; else a code↔first-segment match;
 * else `defaultCode` / first locale.
 */
export function resolveLocaleCodeFromPath(
	switcher: Pick<MegaMenuHeaderLanguageSwitcher, "defaultCode" | "locales">,
	pathname: string | null | undefined
): string {
	const locales = switcher.locales;
	const fallback =
		switcher.defaultCode.trim() || locales[0]?.code.trim() || "US";

	if (!pathname?.trim() || locales.length === 0) {
		return fallback;
	}

	let bestHrefCode: string | null = null;
	let bestHrefLen = -1;
	for (const loc of locales) {
		const href = loc.href?.trim();
		if (!(href && pathMatchesLocaleHref(pathname, href))) {
			continue;
		}
		const pathLen = normalizeNavigationPath(href).length;
		if (pathLen > bestHrefLen) {
			bestHrefLen = pathLen;
			bestHrefCode = loc.code;
		}
	}
	if (bestHrefCode) {
		return bestHrefCode;
	}

	for (const loc of locales) {
		if (localeCodeMatchesPathSegment(loc.code, pathname)) {
			return loc.code;
		}
	}

	return fallback;
}

/**
 * Whether this locale option is already current for the URL (disable in the
 * switcher — do not navigate to the same place).
 * Matches optional `href` (exact / prefix) or locale `code` as the first path segment.
 */
export function isLocaleCurrentForPath(
	locale: MegaMenuLocaleOption,
	pathname: string | null | undefined
): boolean {
	if (!pathname?.trim()) {
		return false;
	}
	const href = locale.href?.trim();
	if (href && pathMatchesLocaleHref(pathname, href)) {
		return true;
	}
	return localeCodeMatchesPathSegment(locale.code, pathname);
}
